import React from "react";
import { Box, Typography, Chip, CircularProgress } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { BADGE_COLORS, getBadgeStyles, getAlmacenColor } from "../components/ui/badgeColors";
import { TipoBadge, EstadoBadge, StockDisplay } from "../components/ui";
import { PETICION_ESTADO, PETICION_TIPO } from "../constants";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import DateUtils from "../../../utils/DateUtils";
import AuthService from "../../../services/auth.service";

/**
 * Columnas del DataGrid del panel de peticiones (vista de reponedores)
 */
export const generatePanelPeticionesColumns = ({
    getNombreAlmacen,
    processingAction,
    onAsignarme,
    onDesasignar,
    onMarcarPreparado,
    onTraspasar,
    onReferenciaClick,
}) => {
    const usuarioActualId = AuthService.getCurrentUser()?.id;

    return [

            {
                field: 'id',
                headerName: 'ID',
                width: 60,
                renderCell: (params) => (
                    <Typography sx={{ fontWeight: 600, fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                        #{params.value}
                    </Typography>
                ),
            },
            {
                field: 'referencia',
                headerName: 'Ref',
                width: 100,
                renderCell: (params) => (
                    <Typography
                        onClick={() => onReferenciaClick(params.value)}
                        sx={{
                            fontWeight: 600,
                            fontSize: DATA_GRID_FONT_SIZE.CELDA,
                            color: "#cc071e",
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": {
                                color: "#a30618",
                            },
                        }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: 'nombreProducto',
                headerName: 'Producto',
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'stock',
                headerName: 'Stock',
                width: 160,
                renderCell: (params) => (
                    <StockDisplay
                        stock={params.row.stock}
                        getNombreAlmacen={getNombreAlmacen}
                        variant="compact"
                        maxVisible={99}
                    />
                ),
            },
            {
                field: 'unidades',
                headerName: 'Uds',
                width: 70,
                type: 'number',
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={getBadgeStyles(BADGE_COLORS.GRAY)}
                    />
                ),
            },
            {
                field: 'numeroAlbaran',
                headerName: 'Albarán',
                width: 100,
                renderCell: (params) => (
                    <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                        {params.value || '-'}
                    </Typography>
                ),
            },
            {
                field: 'tipo',
                headerName: 'Tipo',
                width: 110,
                renderCell: (params) => {
                    const tipo = params.value || PETICION_TIPO.PETICION.value;
                    return <TipoBadge tipo={tipo} />;
                }
            },
            {
                field: 'estado',
                headerName: 'Estado',
                width: 120,
                renderCell: (params) => <EstadoBadge estado={params.value} />
            },
            {
                field: 'almacenesAsignados',
                headerName: 'Almacenes',
                width: 140,
                renderCell: (params) => {
                    const almacenes = params.row.almacenesAsignados || [];
                    if (almacenes.length === 0) {
                        return (
                            <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: "#6c757d" }}>
                                Sin asignar
                            </Typography>
                        );
                    }
                    return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                            {almacenes.map((almacen) => {
                                const colorAlmacen = getAlmacenColor(almacen);
                                return (
                                    <Chip
                                        key={almacen}
                                        label={getNombreAlmacen(almacen) || almacen}
                                        size="small"
                                        sx={getBadgeStyles(colorAlmacen, { height: 22, fontSize: 10 })}
                                    />
                                );
                            })}
                        </Box>
                    );
                }
            },
            {
                field: 'reponedor',
                headerName: 'Asignado',
                width: 100,
                valueGetter: (params) => {
                    return params.row.reponedor?.username || 'Sin asignar';
                },
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontSize: DATA_GRID_FONT_SIZE.CELDA,
                            color: params.value === 'Sin asignar' ? "#6c757d" : "#495057",
                        }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: 'fechaAsignacion',
                headerName: 'F. asignado',
                width: 130,
                valueFormatter: (params) => params.value ? DateUtils.formatearFechaHora(params.value) : '-'
            },
            {
                field: 'fechaPreparado',
                headerName: 'F. preparado',
                width: 130,
                valueFormatter: (params) => params.value ? DateUtils.formatearFechaHora(params.value) : '-'
            },
            {
                field: 'creado',
                headerName: 'F. creación',
                width: 130,
                valueFormatter: (params) => DateUtils.formatearFechaHora(params.value)
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Acciones',
                width: 100,
                sortable: false,
                filterable: false,
                getActions: ({ id, row }) => {
                    // Verificar qué acción específica está en proceso
                    const asignandoEsta = processingAction === `${id}-asignar`;
                    const desasignandoEsta = processingAction === `${id}-desasignar`;
                    const preparandoEsta = processingAction === `${id}-preparado`;
                    const traspasandoEsta = processingAction === `${id}-traspasar`;

                    // Icono de carga
                    const iconoCarga = <CircularProgress size={16} />;

                    const acciones = [];

                    // "Asignarme" solo visible si la peticion aun no tiene reponedor asignado.
                    if (row.estado === PETICION_ESTADO.NO_ATENDIDO.value) {
                        acciones.push(
                            <GridActionsCellItem
                                key="asignarme"
                                icon={asignandoEsta ? iconoCarga : <PersonAddIcon />}
                                label="Asignarme"
                                onClick={() => onAsignarme(id)}
                                disabled={asignandoEsta}
                                sx={{ color: '#495057' }}
                                title="Asignarme esta peticion"
                            />
                        );
                    }

                    // "Traspasar" solo visible si:
                    // - La petición es tipo PETICION (NO reposición)
                    // - La petición está NO_ATENDIDO
                    // - Está asignada al almacén 03
                    // - Hay varios almacenes con stock disponible
                    const almacenesConStock = row.stock?.filter(s => s.cantidad > 0).length || 0;
                    const puedeTraspasarse = row.tipo === PETICION_TIPO.PETICION.value
                        && row.estado === PETICION_ESTADO.NO_ATENDIDO.value
                        && row.almacenesAsignados?.includes('03')
                        && almacenesConStock > 1;

                    if (puedeTraspasarse) {
                        acciones.push(
                            <GridActionsCellItem
                                key="traspasar"
                                icon={traspasandoEsta ? iconoCarga : <CompareArrowsIcon />}
                                label="Traspasar"
                                onClick={() => onTraspasar(id)}
                                disabled={traspasandoEsta}
                                sx={{ color: '#ffc107' }}
                                title="Traspasar a otros almacenes"
                            />
                        );
                    }

                    // "Desasignar" y "Marcar preparado" solo visibles si el reponedor actual es quien la tiene asignada.
                    // Comparacion con String() en ambos lados porque el id del servicio mock puede ser
                    // number mientras que AuthService puede retornar string segun la fuente del JWT.
                    if (row.estado === PETICION_ESTADO.EN_PROCESO.value && String(row.reponedor?.id) === String(usuarioActualId)) {
                        acciones.push(
                            <GridActionsCellItem
                                key="desasignar"
                                icon={desasignandoEsta ? iconoCarga : <PersonRemoveIcon />}
                                label="Desasignar"
                                onClick={() => onDesasignar(id)}
                                disabled={desasignandoEsta}
                                sx={{ color: '#6c757d' }}
                                title="Desasignar esta peticion"
                            />
                        );
                        acciones.push(
                            <GridActionsCellItem
                                key="marcar-preparado"
                                icon={preparandoEsta ? iconoCarga : <TaskAltIcon />}
                                label="Marcar preparado"
                                onClick={() => onMarcarPreparado(id)}
                                disabled={preparandoEsta}
                                color="success"
                                title="Marcar esta peticion como preparada"
                            />
                        );
                    }

                    return acciones;
                }
            },
    ];
};
